
import { EventEmitter, inject, Injectable } from '@angular/core';
import { PlexEnvironment } from '@app/env';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { ColumnMap } from 'app/common/services/data-api/column-map';
import { OdataColumnMapper } from 'app/common/services/data-api/column-mapper';
import { IChangeRequestViewModel } from 'app/shared/interfaces/ichange-request-view-model';
import { Area, AreaType, AssetTeam, ChangeRequest, Scenario, ScenarioChangeRequest, Table } from 'app/shared/models';
import { AreaTypes } from 'app/shared/models/area-types';
import { Column } from 'app/shared/models/column';
import { SimpleWell } from 'app/shared/models/common/simpleWell';
import { DescriptionSorter, NameValueInt } from 'app/shared/models/name-value';
import { ScenarioDrillGroupFilterCriteria } from 'app/shared/models/scenario-drill-group-filter-criteria';
import { BehaviorSubject, Observable, zip as observableZip } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataApi } from './data-api.service';
import { DataLookupService } from './data-lookup.service';

@Injectable({
  providedIn: 'root'
})
export class AppStorage {
  private api = inject(DataApi);
  private lookup = inject(DataLookupService);
  public environment = inject(PlexEnvironment);

  private keyedTables: KeyedBehaviors = {};

  public knownTables: AppStorageTables = null;
  public knownColumns: AppStorageColumns = new AppStorageColumns();
  public knownEvents: AppStorageEvents = new AppStorageEvents();

  public anyTable: KeyedBehaviors = {};
  public eventEmitters: KeyedEmitters = {};

  public globalStore: AnyTableObject = {};
  public knownStore: AppStorageObjects = new AppStorageObjects(this);

  constructor() {
    this.knownTables = new AppStorageTables();

    let scenarioFilter = {
      ScenarioStatusId: [1, 4, 10, 20, 30, 40, 50]   // ScenarioStatuses.Open
    };

    let state: State = {
      filter: this.lookup.buildFilter(scenarioFilter, 'and') as CompositeFilterDescriptor
    };

    let scenarioSelect = 'select=ScenarioId,ScenarioNumber,Name,ScenarioTypeId,Description,StartDate,EndDate,PublishId,SortOrder,AssetTeamId,ScenarioStatusId,BuildEndDate,BuildStartDate,IsLiveScenario,Created,CreatedBy,Updated,UpdatedBy';
    let scenariosSvc = this.api.getService(Table.Scenarios, null, null, state).query(true, state, '', null, scenarioSelect);
    let assetTeamsSub = this.api.getService(Table.AssetTeams, null, null, null).query(false, null, '');

    scenariosSvc.then(r => {
      return Scenario.map(r as Scenario[]);
    }).then(result => {
      if (result) {
        this.knownTables.scenarios.next(result as Scenario[]);
      }
    });

    let areaMaps: ColumnMap[] = [
      new ColumnMap('AreaId', 'AreaId'),
      new ColumnMap('Description', 'Description'),
      new ColumnMap('AreaTypeId', 'AreaTypeId'),
      new ColumnMap('AreaTeamId', 'AreaTeamId'),
      new ColumnMap('SortOrder', 'SortOrder')
    ];
    let areaMapper = new OdataColumnMapper<Area>(areaMaps);
    let areaReq = this.api.getService(Table.Areas, areaMapper, true).query(true, '&$filter=AreaTypeId lt 17 or AreaTypeId gt 19');
    areaReq.then(result => {
      if (result && result.length) {
        this.knownTables.areas.next(result as Area[]);
        let sorted = result.sort(DescriptionSorter);
        this.knownTables.sortedAreas.next(sorted);

        let sortedNVP = sorted.map(ar => { return { name: ar.Description, value: ar.AreaId }; });
        this.knownTables.sortedAreasNameValue.next(sortedNVP);
      }
    });

    let areaTypes = this.api.getService(Table.AreaTypes).query();
    areaTypes.then(result => {
      this.knownTables.areaTypes.next(result as AreaType[]);
    });

    observableZip(scenariosSvc, assetTeamsSub) //, changeRequestWells)
      .subscribe(r => {
        try {
          let scenarios: Scenario[] = r[0] as Scenario[];
          let assetTeams: AssetTeam[] = r[1] as AssetTeam[];

          if (this.environment.hideAllButPATAndDELBasins) {
            // Display only PAT and DEL basin
            assetTeams = assetTeams.filter(at => at.AssetTeamId === 3 || at.AssetTeamId === 8);
          }

          if (!this.environment.showDelawareBasin) {
            // Exclude DEL basin
            assetTeams = assetTeams.filter(at => at.AssetTeamId != 8);
          }

          this.knownTables.assetTeams.next(assetTeams);

          if (!(scenarios)) {
            return;
          }
          let mcr = new Map<number, ChangeRequest>();
          let ms = new Map<number, Scenario>();
          let currentScenarioIdStr = sessionStorage.getItem('selected-scenario-id');
          let currentScenarioId: number = 0;
          if (currentScenarioIdStr) {
            currentScenarioId = parseInt(currentScenarioIdStr, 10);
          }
          scenarios.forEach(s => {
            ms.set(s.ScenarioId, s);
            if (s.ScenarioId === currentScenarioId) {
              //find the assetTeam
              let currentAssetTeamIdStr = sessionStorage.getItem('selected-assetTeam-id') ?? '3';
              let currentAssetTeamId: number = parseInt(currentAssetTeamIdStr, 10);
              let assetTeam = assetTeams.find(at => at.AssetTeamId === currentAssetTeamId);
              if (assetTeam) {
                this.knownStore.assetTeam = assetTeam;
                this.knownStore.scenario = s;
              }
            }
          });

          if (!this.knownStore.scenario) {
            //take the first one from assetteams
            if (this.knownStore.assetTeam) {
              this.knownStore.scenario = scenarios.find(s => s.AssetTeamId === this.knownStore.assetTeam.AssetTeamId);
            } else {
              let first = scenarios.find(s => s.IsLiveScenario && s.AssetTeamId === 3);
              if (first) {
                let assetTeam = assetTeams.find(at => at.AssetTeamId === first.AssetTeamId);
                this.knownStore.assetTeam = assetTeam;
                this.knownStore.scenario = first;
              }
            }
          }
        } catch (e) {
          console.error('Serious unhandled Error inside app-storage-zip:' + e.toString());
        }
      });
  }

  private key(table: Table, key: string) {
    return Table[table] + ':' + key;
  }

  public setNamedTable<T>(table: Table, key: string, set: BehaviorSubject<T>): boolean {
    let thekey = this.key(table, key);
    let existing = this.keyedTables[thekey];
    if (existing) {
      return false;
    } else {
      this.keyedTables[thekey] = set;
      return true;
    }
  }

  public getNamedTable<T>(table: Table, key: string): BehaviorSubject<T[]> {
    let thekey = this.key(table, key);
    return this.keyedTables[thekey];
  }
}

export class AppStorageTables {
  private _scenarios: Scenario[] = [];
  private _changeRequests: ChangeRequest[] = [];
  private _scenarioChangeRequests: ScenarioChangeRequest[] = [];

  public wells: BehaviorSubject<SimpleWell[]> = new BehaviorSubject([]);
  public scenarios: BehaviorSubject<Scenario[]> = new BehaviorSubject([]);
  public scenarioChangeRequests: BehaviorSubject<ScenarioChangeRequest[]> = new BehaviorSubject([]);
  public assetTeams: BehaviorSubject<AssetTeam[]> = new BehaviorSubject([]);
  public scenarioChangeRequestViewModels: BehaviorSubject<IChangeRequestViewModel[]> = new BehaviorSubject([]);
  public changeRequests: BehaviorSubject<ChangeRequest[]> = new BehaviorSubject([]);
  public activeScenarios: BehaviorSubject<Scenario[]> | null;
  public changeRequestsByScenario: Map<number, ChangeRequest[]> = new Map();
  public scenariosByChangeRequest: Map<number, Scenario[]> = new Map();
  public areas: BehaviorSubject<Area[]> = new BehaviorSubject([]);
  public areaTypes: BehaviorSubject<AreaType[]> = new BehaviorSubject([]);
  public sortedAreas: BehaviorSubject<Area[]> = new BehaviorSubject([]);
  public sortedAreasNameValue: BehaviorSubject<NameValueInt[]> = new BehaviorSubject([]);

  public areasByType(areaType: AreaTypes): Observable<Area[]> {
    return this.areas.pipe(map(areas => {
      if (areas && areas.length) {
        return areas.filter(a => a.AreaTypeId === areaType);
      } else {
        return areas;
      }
    }));
  }

  constructor() {
    this.scenarios.subscribe(r => {
      this._scenarios = r;
    });
    this.changeRequests.subscribe(r => {
      this._changeRequests = r;
    });
    this.scenarioChangeRequests.subscribe(r => {
      this._scenarioChangeRequests = r;
    });
  }

  public AddChangeRequest(cr: ChangeRequest) {
    this.AddOne<ChangeRequest>(cr, 'ChangeRequestId', this._changeRequests, '_changeRequests', this.changeRequests);
  }

  public AddScenarioChangeRequest(scr: ScenarioChangeRequest) {
    this.AddOne<ScenarioChangeRequest>(scr, 'ScenarioChangeRequestId',
      this._scenarioChangeRequests, '_scenarioChangeRequests', this.scenarioChangeRequests);
    let scenarios: Scenario[] = this.scenariosByChangeRequest.get(scr.ChangeRequestId);
    if (!scenarios) {
      scenarios = [];
    }
    let scenario = this._scenarios.find(s => s.ScenarioId === scr.ScenarioId);
    let cr = this._changeRequests.find(c => c.ChangeRequestId === scr.ChangeRequestId);

    if (scenario) {
      scenarios.push(scenario);
      this.scenariosByChangeRequest.set(scr.ChangeRequestId, scenarios);
      // TODO - Eric, this needs to be fixed
      if (cr) {
        if (!scenario.ChangeRequests) {
          scenario.ChangeRequests = [];
        }
        scenario.ChangeRequests.push(cr);
      }
    }

    let crs: ChangeRequest[] = this.changeRequestsByScenario.get(scr.ScenarioId);
    if (!crs) {
      crs = [];
    }
    if (cr) {
      crs.push(cr);
      this.changeRequestsByScenario.set(scr.ScenarioId, crs);
    }
    let svms = this.scenarioChangeRequestViewModels.getValue() || [];
    let thesvm = svms.find(svm => svm.ChangeRequestId === scr.ChangeRequestId);
    if (thesvm) {
      thesvm.NumScenarios++;
    }
    this.scenarioChangeRequestViewModels.next(svms);
  }

  private AddOne<t>(data: t, idField: string, source: t[], sourceName: string, ob: BehaviorSubject<t[]>) {
    let updated = false;
    if (source && source.length) {
      source.forEach((t, i) => {
        if (!updated && t[idField] === data[idField]) {
          updated = true;
          source[i] = data;
        }
      });
    }
    if (!updated) {
      if (!source) {
        source = [];
        this[sourceName] = source;
      }
      source.push(data);
    }
    ob.next(source);
  }

  public AddScenario(scenario: Scenario) {
    this.AddOne<Scenario>(scenario, 'ScenarioId', this._scenarios, '_scenarios', this.scenarios);
    let updated = false;
    if (this._scenarios && this._scenarios.length) {
      this._scenarios.forEach((t, i) => {
        if (!updated && t.ScenarioId === scenario.ScenarioId) {
          updated = true;
          this._scenarios[i] = scenario;
        }
      });
    }
    if (!updated) {
      if (!this._scenarios) {
        this._scenarios = [];
      }
      this._scenarios.push(scenario);
    }
    this.scenarios.next(this._scenarios);
  }
}

export class PreventableEventSource<t> {
  private _isDefaultPrevented = false;

  constructor(public data: t) { }

  public preventDefault(): void {
    this._isDefaultPrevented = true;
  }

  public isDefaultPrevented(): boolean {
    return this._isDefaultPrevented;
  }
}

export class AppStorageEvents {
  public scenarioSelecting: EventEmitter<PreventableEventSource<Scenario>> = new EventEmitter();
  public scenarioSelected: BehaviorSubject<Scenario> = new BehaviorSubject(null);
  public assetTeamSelected: BehaviorSubject<AssetTeam> = new BehaviorSubject<AssetTeam>(null);
}

export class AppStorageColumns {
  public scenarios: Column[];
  public wells: Column[];
}

export class AppStorageObjects {
  protected _scenario;
  protected _assetTeam: AssetTeam;

  constructor(private app: AppStorage) { }

  public get scenario(): Scenario {
    return this._scenario;
  }

  public set scenario(source: Scenario) {
    let pe = new PreventableEventSource(source);
    this.app.knownEvents.scenarioSelecting.next(pe);
    if (!pe.isDefaultPrevented()) {
      this._scenario = source;
      sessionStorage.setItem('selected-scenario-id', this._scenario.ScenarioId.toString());
      this.app.knownEvents.scenarioSelected.next(this._scenario);
    }
  }

  public trySetScenario(source: Scenario): Observable<boolean> {
    let result = new Observable<boolean>(observer => {
      let pe = new PreventableEventSource(source);
      this.app.knownEvents.scenarioSelecting.next(pe);
      if (!pe.isDefaultPrevented()) {
        if (!source.PrelimFilterCriteria) {
          source.PrelimFilterCriteria = new ScenarioDrillGroupFilterCriteria();
        }
        this._scenario = source;
        sessionStorage.setItem('selected-scenario-id', this._scenario.ScenarioId.toString());
        this.app.knownEvents.scenarioSelected.next(this._scenario);
        observer.next(true);
      } else {
        observer.next(false);
      }
    });
    return result;
  }

  public lastAssetTeamId(): String {
    return sessionStorage.getItem('selected-assetTeam-id');
  }

  public set assetTeam(source: AssetTeam) {

    this._assetTeam = source;
    sessionStorage.setItem('selected-assetTeam-id', this._assetTeam.AssetTeamId.toString());
    this.app.knownEvents.assetTeamSelected.next(this._assetTeam);
    //now select the liveScenario based on selected asset Team
    this.app.knownTables.scenarios.subscribe(s => {
      let live = s.find(sc => sc.AssetTeamId === source.AssetTeamId && sc.IsLiveScenario);
      if (live) {
        this.scenario = live;
      } else if (s.length) {
        this.scenario = s[0];
      }
    });
  }

  public get assetTeam() {
    return this._assetTeam;
  }

  public trySetAssetTeam(source: AssetTeam): Observable<boolean> {
    let result = new Observable<boolean>(observer => {

      let pe = new PreventableEventSource(source);
      this._assetTeam = source;
      sessionStorage.setItem('selected-assetTeam-id', this._assetTeam.AssetTeamId.toString());
      this.app.knownEvents.assetTeamSelected.next(this._assetTeam);
      this.app.knownTables.scenarios.subscribe(s => {
        let live = s.find(sc => sc.AssetTeamId === source.AssetTeamId && sc.IsLiveScenario);
        if (live) {
          this.scenario = live;
        } else if (s.length) {
          this.scenario = s[0];
        }
      }
      );
      observer.next(true);
    });
    return result;
  }
}

export interface KeyedBehaviors {
  [tableKey: string]: BehaviorSubject<any> | null;
}

export interface KeyedEmitters {
  [key: string]: EventEmitter<any> | null;
}

export interface AnyTableObject {
  [key: string]: object | null;
}